<template>
  <div ref="sidebarRef" class="sidebar" :style="{ top: topOffset }">
    <BaseElInput
      v-if="useSidebarSearch"
      v-model="search"
      style="width: 100%"
      placeholder="搜尋頁面"
      clearable
    />
    <!-- <div class="collapse-btn" @click="closeSidebar">
      <MaterialIcon color="white" class="select-none">
        chevron_left
      </MaterialIcon>
    </div> -->
    <div class="sidebar-wrapper">
      <div
        v-if="sidebarPage === 2"
        id="current-shop-btn"
        class="branch"
        @click="routeTo('/branch-shop-options'), setPage()"
      >
        <span class="material-icons">chevron_left</span>
        <span class="name" :data-testid="`active-shop-${shopName}`">{{ shopName }}</span>
      </div>

      <!-- 組織頁面 -->
      <el-menu
        v-show="sidebarPage === 1"
        :default-active="activeMenu"
        :unique-opened="false"
        :collapse-transition="false"
        mode="vertical"
      >
        <sidebar-item
          v-for="route in displayOrgRoutes"
          :key="route.path"
          :item="route"
          :base-path="route.path"
          :haveUnreadAnnouncements="haveUnreadAnnouncements"
        />
      </el-menu>

      <!-- 動態頁面 -->
      <el-menu
        v-show="sidebarPage === 2 && shop"
        :default-active="activeMenu"
        :unique-opened="false"
        :collapse-transition="false"
        mode="vertical"
      >
        <sidebar-item
          v-for="route in availableAsyncRoutes"
          :key="route.path"
          :item="route"
          :base-path="route.path"
          :haveUnreadAnnouncements="haveUnreadAnnouncements"
        />
      </el-menu>
    </div>

    <div v-if="paramsRoutes.length && sidebarPage === 2" class="setting-btn-container">
      <el-menu mode="vertical" class="w-full">
        <router-link to="/parameter-settings" data-cy="settings-btn">
          <el-menu-item class="settings-btn flex items-center">
            <span class="material-icons" style="font-size: 20px; margin-right: 8px">settings</span>
            <span>{{ $t('parameterSetting.title') }}</span>
          </el-menu-item>
        </router-link>
      </el-menu>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import SidebarItem from './SidebarItem.vue'
import { checkFeature } from '@/store/modules/permission'
import { get, filter, includes, cloneDeep } from 'lodash'
import store from '@/store'
import { refDebounced } from '@vueuse/core'
import { useRoute } from 'vue-router/composables'
import { usePageTabs } from '@/use/usePageTabs'
import { matchRouteSearch } from '@/utils/helper'
import { mapGetters } from 'vuex'

export default defineComponent({
  name: 'Sidebar',
  components: {
    SidebarItem,
  },
  setup () {
    const route = useRoute()
    const { resetTabs } = usePageTabs()
    const search = ref(null)
    const debouncedSearch = refDebounced(search, 200)
    const sidebarRef = ref(null)
    const windowWidth = ref(window.innerWidth)

    const onResize = () => { windowWidth.value = window.innerWidth }
    const onDocClick = (event) => {
      if (windowWidth.value <= 1200 && sidebarRef.value && !sidebarRef.value.contains(event.target)) {
        store.dispatch('app/setSidebar', false)
      }
    }
    onMounted(() => {
      window.addEventListener('resize', onResize)
      document.addEventListener('click', onDocClick)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('resize', onResize)
      document.removeEventListener('click', onDocClick)
    })
    const haveUnreadAnnouncements = computed(() => get(store.state, 'app.haveUnreadAnnouncements'))
    const useSidebarSearch = computed(() => get(store.getters, 'app/useSidebarSearch'))
    const sidebarPage = computed(() => get(store.getters, 'sidebarPage'))

    const displayOrgRoutes = computed(() => {
      const orgAdminRoutes = get(store.getters, 'orgAdminRoutes', [])

      let filteredRoutes = filter(orgAdminRoutes, (route) => !route.hidden)

      // 如果有搜尋條件，進行搜尋過濾
      if (debouncedSearch.value && sidebarPage.value === 1) {
        filteredRoutes = filterSearchRoute(filteredRoutes, debouncedSearch.value)
      }

      return filteredRoutes
    })

    const topOffset = computed(() => {
      if (route.name === 'SalesPOS') {
        return '0px'
      }

      if (windowWidth.value >= 1200) {
        return '0px'
      }

      return '80px'
    })
    // 搜尋功能函式
    const filterSearchRoute = (routes, searchValue) => {
      const cloneRoutes = cloneDeep(routes)
      return filter(cloneRoutes, (route) => {
        // 檢查當前路由是否符合搜尋條件
        if (matchRouteSearch(route, searchValue)) {
          return route
        }

        // 遞迴搜尋子路由
        if (route.children) {
          const filteredChildren = filterSearchRoute(route.children, searchValue)
          if (filteredChildren.length > 0) {
            route.children = filteredChildren
            return route
          }
        }

        return false
      })
    }

    const closeSidebar = () => {
      store.dispatch('app/setSidebar', false)
    }
    return {
      search,
      filterSearchRoute,
      useSidebarSearch,
      haveUnreadAnnouncements,
      closeSidebar,
      sidebarRef,
      topOffset,
      resetTabs,
      displayOrgRoutes,
      debouncedSearch,
    }
  },

  computed: {
    ...mapGetters([
      'permissionRoutes',
      'asyncRoutes',
      'shopName',
      'shop',
      'sidebarPage',
      'userFeatures',
      'sidebar',
    ]),

    paramsRoutes () {
      if (!this.asyncRoutes) return []
      if (this.asyncRoutes) {
        if (!this.asyncRoutes.length) return []
      }
      const routes = this.asyncRoutes.filter((route) => route.name === 'Parameters')
      if (routes) {
        if (!routes.length) return []
      }
      const children = routes[0].children
      return filter(children, (route) => !route.hidden)
    },

    useParamsRoutes () {
      return checkFeature('userFeatures', 'admin.lineConfig.page')
    },

    isMobile () {
      return this.$store.state.app.isMobile
    },

    activeMenu () {
      const route = this.$route
      const { meta, path } = route
      // if set path, the sidebar will highlight the path you set
      if (meta.activeMenu) {
        return meta.activeMenu
      }
      return path
    },

    availableAsyncRoutes () {
      // GIMMATEK 店家首頁不再是公告頁，海外店家、禁用店家公告也照常顯示（CNCY-10767）
      let filteredRoutes = filter(this.asyncRoutes, (route) => !route.hidden)

      // 如果有搜尋條件，進行搜尋過濾
      if (this.debouncedSearch && this.sidebarPage === 2) {
        filteredRoutes = this.filterSearchRoute(filteredRoutes, this.debouncedSearch)
      }

      return filteredRoutes
    },
  },

  beforeMount () {
    const initSidebar = () => {
      const width = window.innerWidth
      if (width <= 1200) {
        this.$store.dispatch('app/setSidebar', false)
        this.$store.dispatch('app/setMobile', true)
      }
      if (width > 1200) {
        if (!includes(['SalesPOS'], this.$route.name)) {
          this.$store.dispatch('app/setSidebar', true)
        }
        this.$store.dispatch('app/setMobile', false)
      }
    }
    initSidebar()
    window.addEventListener('resize', () => {
      if (this.$route.name === 'DailyView') return
      initSidebar()
    })
  },

  methods: {
    routeTo (path) {
      this.$router.push(path)
    },
    async setPage () {
      const page = this.sidebarPage === 1 ? 2 : 1
      await this.$store.dispatch('app/setSidebarPage', page)
      // clear pageTabs
      this.resetTabs()
    },
    clearBranch () {
      this.$store.commit('user/SET_NOW_SHOP', null)
    },
  },
})
</script>

<style lang="scss" scoped>
.branch {
  @apply flex items-center cursor-pointer py-[10px] text-primary-100 px-[15px];
  @apply border border-primary-100 rounded-bl-sm rounded-tr-sm mb-[12px];
  .title {
    @apply font-bold text-[16px] overflow-hidden whitespace-nowrap overflow-ellipsis;
  }
  .name {
    @apply overflow-hidden overflow-ellipsis whitespace-nowrap;
  }
}

.branch:hover {
  @apply bg-primary-100 text-white;
}

/* 側邊欄 menu item */
::v-deep .el-menu {
  @apply border-r-0 bg-transparent;
}

$views-padding: 60px;
$offset-padding: 300px;
$sidebar-width: 260px;

.sidebar {
  @apply relative border border-r-gray-40 bg-[#F7F8FC];
  transition: ease-in-out 0.3s;
}

.sidebar::-webkit-scrollbar {
  @apply hidden;
}

.collapse-btn {
  @apply absolute right-[-12px] top-[50%] select-none cursor-pointer;
  @apply bg-primary-100 rounded-full z-[2];
}

.sidebar-wrapper {
  @apply py-[20px] px-[15px] flex flex-col overflow-x-hidden overflow-y-auto flex-1 whitespace-nowrap;
  @apply relative;
}

.sidebar-wrapper::-webkit-scrollbar {
  @apply hidden;
}

@media screen and (min-width: 1200px) {
  .sidebar {
    @apply relative w-0 h-full overflow-x-hidden flex flex-col;
  }
}

@media screen and (max-width: 1200px) {
  .sidebar {
    @apply absolute w-[260px] left-[-300px] bottom-0 flex-shrink-0 flex flex-col z-[500];
  }

  .sidebar-wrapper {
    @apply h-full;
  }
}

.setting-btn-container {
  @apply bg-[#F7F8FC] px-[15px] relative;
}
.setting-btn-container::after {
  content: '';
  width: 95%;
  @apply h-[1px] bg-gray-40 absolute top-0 left-0 right-0 m-auto;
}

.open {
  @apply left-0;
}

.settings-btn.el-menu-item {
  @apply py-[6px] h-auto rounded-sm my-[14px] leading-normal whitespace-normal;
  /* line-height: unset; */
}

.settings-btn.el-menu-item:hover {
  @apply bg-primary-100 text-white h-auto font-bold;
}
</style>
